import React from 'react';
import './Common.css';

/**
 * Avatar component for displaying user profile pictures or fallback initials
 * 
 * @param {Object} props
 * @param {string} [props.src] - Source URL for avatar image
 * @param {string} [props.alt='User avatar'] - Alt text for the avatar image
 * @param {string} [props.username] - Username to derive fallback initials from
 * @param {string} [props.size='medium'] - Size of avatar (small, medium, large)
 * @param {string} [props.className=''] - Additional class name for styling
 * @returns {JSX.Element}
 */
const Avatar = ({ 
  src, 
  alt = 'User avatar', 
  username,
  size = 'medium',
  className = '',
  ...restProps 
}) => {
  // Default avatar image URL if src is missing AND no username is provided
  const defaultAvatarUrl = 'https://via.placeholder.com/150?text=User'; 
  
  const [imgSrc, setImgSrc] = React.useState(src);
  const [showFallback, setShowFallback] = React.useState(!src);

  React.useEffect(() => {
    setImgSrc(src);
    setShowFallback(!src);
  }, [src]);

  const handleError = () => {
    // If image fails AND we have a username, show initials
    if (username) {
      setShowFallback(true);
    } else {
      // Otherwise, show the default placeholder image
      setImgSrc(defaultAvatarUrl);
      setShowFallback(false); // Ensure we try to show the img tag again
    }
  };

  const fallbackInitial = username ? username.charAt(0).toUpperCase() : '?';

  const avatarClasses = `avatar avatar-${size} ${className}`;

  if (showFallback && username) {
    // Render fallback initials
    return (
      <div 
        className={`avatar-fallback ${avatarClasses}`} 
        aria-label={alt}
        {...restProps}
      >
        <span className="avatar-initial">{fallbackInitial}</span>
      </div>
    );
  }

  // Render image (either original src or default placeholder)
  return (
    <img
      src={imgSrc || defaultAvatarUrl} // Use state imgSrc or fallback URL
      alt={alt}
      className={avatarClasses}
      onError={handleError}
      {...restProps}
    />
  );
};

export default Avatar; 